import { useState, useMemo } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useIsMedium } from '@/utils/hooks';
import { getOr } from '@/utils/options';
import Button, { variantFromString } from 'components/src/button';
import TextInput from 'components/src/textInput/TextInput';
import { FormInputFieldsFragment, FormTabsFieldsFragment } from 'types/generated';
import HiddenInputFields from '../../SalesForce/HiddenInputFields';
import typewriter, { DoBusinessWithUsFormSubmittedType } from '@/analytics/segment';
import KennitalaInput from '../../common/KennitalaInput';
import PhonenumberInput from '../../common/PhoneNumberInput';

type Props = {
  tab: FormTabsFieldsFragment | null;
  isCallMe?: boolean;
  prependDescription?: string;
  callback?: () => void;
};

const FormTabContent = ({ tab, isCallMe = false, prependDescription = '' }: Props) => {
  const isMedium = useIsMedium();
  const [inputValues, setInputValues] = useState<Record<string, string>>(
    tab?.content?.__typename === 'Form'
      ? tab?.content?.inputFieldsCollection?.items?.reduce<Record<string, string>>((acc, curr) => {
          // ID is required in Contentful, we need getOr here for type checking
          acc[getOr(curr?.id, 'NO_ID')] = '';
          return acc;
        }, {}) || {}
      : {}
  );
  const [verifiedTimestamp, setVerifiedTimestamp] = useState<string | null>(
    process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ? null : JSON.stringify(new Date().getTime())
  );
  const [hasError, setHasError] = useState<boolean>(false);

  const submitDisabled = useMemo(() => {
    const hasEmptyFields = Object.values(inputValues).some((value) => !value.trim());
    return hasError || !verifiedTimestamp || hasEmptyFields;
  }, [inputValues, hasError, verifiedTimestamp]);

  const handleChange = (inputField: FormInputFieldsFragment | null, value: string) => {
    setInputValues((prev) => ({ ...prev, [getOr(inputField?.id, 'NO_ID')]: value }));
  };

  if (tab?.content?.__typename !== 'Form') {
    return null;
  }
  // If there's some input field with name "company" then we're rendering the company form
  let type = DoBusinessWithUsFormSubmittedType.CallMe;
  if (!isCallMe) {
    const isB2B = tab?.content?.inputFieldsCollection?.items.some((item) => item?.name === 'company');
    type = isB2B ? DoBusinessWithUsFormSubmittedType.Company : DoBusinessWithUsFormSubmittedType.Home;
  }
  const isB2B = tab?.content?.inputFieldsCollection?.items.some((item) => item?.name === 'company');

  return (
    <>
      <form
        action={`https://${process.env.NEXT_PUBLIC_SF_ENDPOINT_PREFIX}.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8&orgId=${process.env.NEXT_PUBLIC_SF_ORG_ID}`}
        method="POST"
        onSubmit={() => {
          typewriter.doBusinessWithUsFormSubmitted({
            type: type,
          });
        }}
        className="flex flex-col gap-8"
      >
        <HiddenInputFields verifiedTimestamp={verifiedTimestamp} isB2B={Boolean(isB2B)} />
        {tab.content.inputFieldsCollection?.items?.map((inputField, index) => {
          if (inputField?.inputType === 'kennitala') {
            return (
              <KennitalaInput
                key={index}
                name={getOr(inputField?.name, '')}
                id={getOr(inputField?.id, '')}
                label={getOr(inputField?.title, '')}
                setValidationError={setHasError}
                placeholder={getOr(inputField.placeholder, '')}
                onChange={(value) => handleChange(inputField, value)}
              />
            );
          } else if (inputField?.inputType === 'tel') {
            return (
              <PhonenumberInput
                key={index}
                id={getOr(inputField?.id, '')}
                name={getOr(inputField?.name, '')}
                label={getOr(inputField?.title, '')}
                placeholder={getOr(inputField.placeholder, '')}
                onChange={(value) => handleChange(inputField, value)}
              />
            );
          } else if (inputField?.id && inputField?.name === 'description' && !!prependDescription) {
            return (
              <>
                <TextInput
                  key={index}
                  id={`${inputField.id}-facad`}
                  name={`${inputField.id}-facad`}
                  label={getOr(inputField?.title, '')}
                  placeholder={getOr(inputField?.placeholder, '')}
                  type={getOr(inputField?.inputType, 'text')}
                  value={`${inputValues[inputField?.id || 'NO_ID']}`}
                  onChange={(value) => handleChange(inputField, value)}
                />
                <TextInput
                  key={index}
                  id={inputField.id}
                  name={inputField.name}
                  type={'hidden'}
                  value={`${prependDescription}: ${inputValues[inputField?.id || 'NO_ID']}`}
                />
              </>
            );
          } else {
            return (
              <TextInput
                key={index}
                id={getOr(inputField?.id, '')}
                name={getOr(inputField?.name, '')}
                label={getOr(inputField?.title, '')}
                placeholder={getOr(inputField?.placeholder, '')}
                type={getOr(inputField?.inputType, 'text')}
                value={inputValues[inputField?.id || 'NO_ID']}
                onChange={(value) => handleChange(inputField, value)}
              />
            );
          }
        })}
        {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
          <ReCAPTCHA
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
            onChange={() => setVerifiedTimestamp(JSON.stringify(new Date().getTime()))}
            onExpired={() => setVerifiedTimestamp(null)}
            onErrored={() => setVerifiedTimestamp(null)}
          />
        )}
        <Button
          type="submit"
          label={tab.content.button?.label || ''}
          classes="px-64"
          variant={variantFromString(tab.content.button?.variant)}
          autoWidth={isMedium}
          disabled={submitDisabled}
        />
      </form>
    </>
  );
};

export default FormTabContent;
